/**
 * A designed post, drawn — the one renderer, used everywhere.
 *
 * The same component draws the composer's canvas, the post page, and the feed. That
 * is not a convenience: a design is a composition, and two renderers would be two
 * compositions — the author would be arranging one thing and publishing another.
 * The editor puts an invisible input on top of *this* (`DesignCanvas`) rather than
 * drawing its own version of it.
 *
 * Two things are the whole reason it is drawn natively rather than exported as an
 * image: the words stay the post's own words (legible to a screen reader, selectable,
 * translatable), and nothing about a design is heavier than the sentence it holds.
 *
 * The layers, in order:
 *
 *  1. the colour the design falls back to — always painted, so a reader whose device
 *     cannot fetch a picture still gets a coloured design rather than a hole;
 *  2. the picture, when there is one, either across the whole composition or in the
 *     band the template asked for;
 *  3. the grain, laid over the picture as a native overlay — never baked into it;
 *  4. the template's own geometry, as SVG paths, fitted to the canvas;
 *  5. the words, in the template's slot, with their highlights and Mursala;
 *  6. the template's ornament.
 *
 * **The words are held back until the face is in.** A design drawn a moment early is
 * a design drawn in the system Arabic font — the exact failure this feature exists to
 * avoid — so the text is painted transparent until `useThmanyahFonts` says the
 * shipped faces are loaded, and the box keeps its height in the meantime.
 *
 * **Children are handed the very metrics the words were drawn with.** The editor's
 * input takes them as a render prop, so it cannot disagree with the drawing about a
 * single value: there is one calculation (`#/state/postStyle/layout`) and one reader
 * of it per drawn line.
 *
 * **The words are drawn by React Native's own `Text`, not the app's.** The app's text
 * component resolves its voice from the theme — it rewrites `fontFamily` to Inter on
 * every platform and appends its own `fontVariant` — which is right for the app and
 * wrong for a design: a designed post is a picture of a sentence, its face is part of
 * what the author chose, and a design quietly re-typed in Inter is exactly the
 * failure this feature exists to avoid. So this file draws with the primitive and
 * says its own face; the app's component is used everywhere else.
 */
import {type ReactNode, useCallback, useState} from 'react'
import {
  Image,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
  type ViewStyle,
} from 'react-native'
import Svg, {Path} from 'react-native-svg'

import {mursalaStyle, useThmanyahFonts} from '#/state/postStyle/fonts'
import {
  designSlotPadding,
  designTextMetrics,
  type DesignTextMetrics,
} from '#/state/postStyle/layout'
import {
  DESIGN_SELECTION_WASH,
  PATH_FROM_AUTHOR,
  STYLE_STICKERS,
  styledTemplate,
  templateAuthorPathColour,
  templateLayoutPx,
  templateVerticalAlign,
} from '#/state/postStyle/templates'
import {
  resolveStyledPost,
  styledPostSegments,
} from '#/state/postStyle/typography'

/** The grain a design may be laid over, generated once (`assets/images/stylizer`). */
const GRAIN = require('../../../assets/images/stylizer/grain.png')

/** The shortest a design is drawn; a longer one grows with its own words. */
export const DESIGN_MIN_HEIGHT = 200

/**
 * The wash a template's picture slot shows before the author has chosen a picture.
 *
 * A band that was simply absent would make an image-first template look like a
 * mistake; a band tinted with the design's own ink reads as the place a picture
 * goes — and says so without a label.
 */
const EMPTY_SLOT_OPACITY = 0.08

export function PostDesignRenderer({
  style,
  text,
  backgroundImageUri,
  selection,
  minHeight = DESIGN_MIN_HEIGHT,
  testID = 'postDesign',
  children,
}: {
  style: ReturnType<typeof resolveStyledPost>
  text: string
  /** The author's own picture, where a reader or the author's own device has it. */
  backgroundImageUri?: string
  /**
   * The author's own selection, while they are editing.
   *
   * Passed by the editor alone, and drawn *by the design* — on the design's words, at
   * the design's size — because the editor's own selection is invisible (its text is
   * what the design draws) and a browser's version of it is a block of line boxes that
   * belongs to no word on the screen. Never part of the mark.
   */
  selection?: {start: number; end: number} | null
  /** The shortest the composition is drawn, in points. */
  minHeight?: number
  testID?: string
  /**
   * The editing layer, drawn *inside* the template's text slot and handed the same
   * metrics the words are drawn with.
   */
  children?: (metrics: DesignTextMetrics) => ReactNode
}) {
  const fontsReady = useThmanyahFonts()
  const [width, setWidth] = useState(0)

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = Math.round(event.nativeEvent.layout.width)
    setWidth(current => (current === measured ? current : measured))
  }, [])

  const resolved = resolveStyledPost(style, text)
  const template = resolved.template ? styledTemplate(resolved.template) : null
  const templatePx = templateLayoutPx(template, width)
  const background = resolved.background
  const ink = resolved.ink ?? '#1A1A1A'

  /*
   * A band's picture sits above the words; a full-bleed picture is behind them. Both
   * are the *same* picture — the one the author chose — which is what makes a
   * template's image slot and a plain image background one feature rather than two.
   *
   * A picture is drawn only while the design *claims* one. The editor knows a file the
   * mark does not yet describe — the author's own pick, before it has travelled — and
   * that file must stop being drawn the moment the background goes back to a colour,
   * or "remove the picture" would remove it from the mark and leave it on the screen.
   */
  const picture = background.type === 'image' ? backgroundImageUri : undefined
  const bandHeight = templatePx.imageBand > 0 ? templatePx.imageBand : undefined
  const slotPadding = designSlotPadding(width, {
    padTop:
      templatePx.padTop + (templatePx.imageAboveText ? (bandHeight ?? 0) : 0),
    padBottom: templatePx.padBottom,
  })
  const metrics =
    width > 0
      ? designTextMetrics(resolved, {
          chars: text.length,
          width,
          text,
        })
      : null

  const authorPathColour = templateAuthorPathColour(template, resolved)
  const pathColour = (value?: string) =>
    value === PATH_FROM_AUTHOR ? authorPathColour : value

  const slotStyle: ViewStyle = {
    flex: 1,
    justifyContent: templateVerticalAlign(template),
    paddingTop: slotPadding.paddingTop,
    paddingBottom: slotPadding.paddingBottom,
    paddingHorizontal: slotPadding.paddingHorizontal,
  }

  const segments = styledPostSegments(resolved, text, selection)
  const words = metrics ? (
    <Text
      testID={`${testID}-text`}
      style={{
        fontFamily: metrics.fontFamily,
        fontSize: metrics.fontSize,
        lineHeight: metrics.lineHeight,
        textAlign: metrics.textAlign,
        color: metrics.color,
        /* Held back until the shipped face is in — never drawn in a substitute. */
        opacity: fontsReady ? 1 : 0,
      }}>
      {segments.map((segment, index) => (
        <Text
          key={index}
          style={[
            segment.highlight ? {backgroundColor: segment.highlight} : null,
            segment.selected ? {backgroundColor: DESIGN_SELECTION_WASH} : null,
            segment.mursala ? mursalaStyle() : null,
          ]}>
          {segment.text}
        </Text>
      ))}
    </Text>
  ) : null

  return (
    <View
      testID={testID}
      onLayout={onLayout}
      style={[styles.canvas, {minHeight, backgroundColor: background.color}]}
      /*
       * In the feed it is a poster, but it is *text*: a screen reader reads the
       * words, in order, exactly as it would from a plain paragraph. While it is
       * being edited it is not the accessible element — the input on top of it is.
       */
      accessible={!children}
      accessibilityRole="text"
      accessibilityLabel={children ? undefined : text}>
      {picture && !templatePx.imageBand ? (
        <Image
          testID={`${testID}-background`}
          source={{uri: picture}}
          style={StyleSheet.absoluteFill}
          resizeMode="cover"
          accessible={false}
        />
      ) : null}

      {picture && templatePx.imageBand ? (
        <Image
          testID={`${testID}-background`}
          source={{uri: picture}}
          style={[
            styles.band,
            templatePx.imageAboveText
              ? {top: 0, left: 0, right: 0, height: bandHeight}
              : {bottom: 0, left: 0, right: 0, height: bandHeight},
          ]}
          resizeMode="cover"
          accessible={false}
        />
      ) : null}

      {/* The place a template's picture goes, before the author has chosen one. */}
      {!picture && templatePx.imageBand ? (
        <View
          testID={`${testID}-slot`}
          style={[
            styles.band,
            templatePx.imageAboveText
              ? {top: 0, left: 0, right: 0, height: bandHeight}
              : {bottom: 0, left: 0, right: 0, height: bandHeight},
            {backgroundColor: ink, opacity: EMPTY_SLOT_OPACITY},
          ]}
        />
      ) : null}

      {resolved.texture ? (
        <Image
          testID={`${testID}-texture`}
          source={GRAIN}
          style={[StyleSheet.absoluteFill, styles.grain]}
          resizeMode="repeat"
          accessible={false}
        />
      ) : null}

      {template && template.paths.length > 0 ? (
        <Svg
          testID={`${testID}-paths`}
          viewBox="0 0 100 100"
          /* Stretched to the canvas so one composition is that composition at every
           * width; the paths are authored to survive it (rules, arcs, scrims). */
          preserveAspectRatio="none"
          style={StyleSheet.absoluteFill}
          pointerEvents="none">
          {template.paths.map((path, index) => (
            <Path
              key={index}
              d={path.d}
              fill={pathColour(path.fill) ?? 'none'}
              stroke={pathColour(path.stroke)}
              strokeWidth={path.strokeWidth}
              opacity={path.opacity}
            />
          ))}
        </Svg>
      ) : null}

      <View style={slotStyle}>
        {metrics ? (
          children ? (
            /*
             * The *drawn words* are what the box is: the slot centres this box, so the
             * composition sits where the design says it does, and the editing layer is
             * laid over it rather than beside it.
             *
             * This is not a detail of arrangement. An input in the flow decides the
             * box's height, and a browser is free to make its editor taller than its
             * own text — so the words would be drawn at one height, the caret and the
             * selection at another, and selecting a long post would paint a column the
             * height of the composition over words it was not aligned with. One box,
             * sized by the words: one thing to edit.
             */
            <View
              style={[
                styles.textBox,
                /* An empty design has no words to size it, and still needs a line to
                   be typed into. */
                text ? null : {minHeight: metrics.lineHeight},
              ]}>
              {words}
              <View style={StyleSheet.absoluteFill}>{children(metrics)}</View>
            </View>
          ) : (
            words
          )
        ) : null}
      </View>

      {resolved.sticker && STYLE_STICKERS[resolved.sticker] ? (
        <Text
          testID={`${testID}-sticker`}
          style={[styles.sticker, {color: ink, opacity: fontsReady ? 0.85 : 0}]}
          accessible={false}>
          {STYLE_STICKERS[resolved.sticker]}
        </Text>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  canvas: {
    borderRadius: 18,
    overflow: 'hidden',
    justifyContent: 'flex-start',
  },
  textBox: {
    width: '100%',
  },
  /*
   * A band is taken out of the flow: it is a strip of the composition, not a block
   * above the words, and the slot's own padding already reserves the room for it.
   */
  band: {
    position: 'absolute',
  },
  grain: {
    opacity: 0.5,
  },
  sticker: {
    position: 'absolute',
    top: 14,
    right: 18,
    fontSize: 30,
  },
})
